import { Modal, Form, Select, DatePicker, Input, App } from 'antd'
import { useState } from 'react'
import { grantAccess } from '../services/internal-access-api'
import {
  InternalAccessRole,
  roleLabels,
  type ZealysLogin,
  type GrantAccessRequest
} from '../types/internal-access.types'
import dayjs, { type Dayjs } from 'dayjs'

interface GrantAccessModalProps {
  open: boolean
  login: ZealysLogin | null
  onClose: () => void
  onSuccess: () => void
}

export function GrantAccessModal({ open, login, onClose, onSuccess }: GrantAccessModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const request: GrantAccessRequest = {
        loginId: login!.id,
        role: values.role,
        expiryDate: values.expiryDate ? values.expiryDate.toISOString() : null,
        notes: values.notes || ''
      }

      await grantAccess(request)
      message.success('Access granted successfully')
      form.resetFields()
      onSuccess()
      onClose()
    } catch (error: any) {
      if (error.errorFields) {
        return
      }
      message.error(error.response?.data?.message || 'Failed to grant access')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={`Grant Access - ${login?.loginName || login?.email}`}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Grant Access"
      width={600}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select a role' }]}>
          <Select placeholder="Select role" showSearch optionFilterProp="children">
            <Select.OptGroup label="Workspace">
              <Select.Option value={InternalAccessRole.WORKSPACE_ADMIN}>
                {roleLabels[InternalAccessRole.WORKSPACE_ADMIN]}
              </Select.Option>
              <Select.Option value={InternalAccessRole.WORKSPACE_APPROVER}>
                {roleLabels[InternalAccessRole.WORKSPACE_APPROVER]}
              </Select.Option>
              <Select.Option value={InternalAccessRole.WORKSPACE_USER}>
                {roleLabels[InternalAccessRole.WORKSPACE_USER]}
              </Select.Option>
            </Select.OptGroup>
            <Select.OptGroup label="Billing">
              <Select.Option value={InternalAccessRole.BILLING_ADMIN}>
                {roleLabels[InternalAccessRole.BILLING_ADMIN]}
              </Select.Option>
              <Select.Option value={InternalAccessRole.BILLING_USER}>
                {roleLabels[InternalAccessRole.BILLING_USER]}
              </Select.Option>
            </Select.OptGroup>
            <Select.OptGroup label="Requester">
              <Select.Option value={InternalAccessRole.REQUESTER_SALES}>
                {roleLabels[InternalAccessRole.REQUESTER_SALES]}
              </Select.Option>
              <Select.Option value={InternalAccessRole.REQUESTER_CONSULTANT}>
                {roleLabels[InternalAccessRole.REQUESTER_CONSULTANT]}
              </Select.Option>
              <Select.Option value={InternalAccessRole.REQUESTER_FINANCE}>
                {roleLabels[InternalAccessRole.REQUESTER_FINANCE]}
              </Select.Option>
            </Select.OptGroup>
          </Select>
        </Form.Item>

        <Form.Item name="expiryDate" label="Expiry Date (Optional)">
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={(current: Dayjs) => current && current < dayjs().startOf('day')}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        <Form.Item name="notes" label="Notes (Optional)">
          <Input.TextArea rows={4} maxLength={1000} showCount placeholder="Add any notes about this access grant" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
